import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { ImageModalV2Mode } from './useImageModalV2Mode';
import {
    IMAGE_MODAL_V2_TOOLBAR_EXIT_FALLBACK_MS,
    IMAGE_MODAL_V2_TOOLBAR_MOTION_DURATION_MS,
} from '../imageModalV2Constants';

const SECONDARY_TOOLBAR_EXIT_FALLBACK_MS =
    IMAGE_MODAL_V2_TOOLBAR_MOTION_DURATION_MS + IMAGE_MODAL_V2_TOOLBAR_EXIT_FALLBACK_MS;

const isImageModalV2EditorMode = (mode: ImageModalV2Mode): boolean =>
    mode === ImageModalV2Mode.CROP || mode === ImageModalV2Mode.BACKGROUND || mode === ImageModalV2Mode.DRAW;

/**
 * Holds an outgoing editor mode until its portaled toolbar has visibly finished exiting.
 */
export const useImageModalV2PresentedMode = ({ elementId, mode }: { elementId: string; mode: ImageModalV2Mode }) => {
    const [presentedMode, setPresentedMode] = useState(mode);
    const desiredModeRef = useRef(mode);
    const previousElementIdRef = useRef(elementId);
    desiredModeRef.current = mode;

    useLayoutEffect(() => {
        const elementChanged = previousElementIdRef.current !== elementId;
        previousElementIdRef.current = elementId;

        if (elementChanged || isImageModalV2EditorMode(mode) || !isImageModalV2EditorMode(presentedMode)) {
            setPresentedMode(mode);
            return undefined;
        }

        const fallback = window.setTimeout(() => setPresentedMode(mode), SECONDARY_TOOLBAR_EXIT_FALLBACK_MS);
        return () => window.clearTimeout(fallback);
    }, [elementId, mode, presentedMode]);

    const completeSecondaryToolbarExit = useCallback(() => {
        setPresentedMode(desiredModeRef.current);
    }, []);

    return { presentedMode, completeSecondaryToolbarExit };
};
